import { useCallback, useRef, useState } from "react";
import API_BASE, { withApiCredentials } from "../../api";
import { trackProductEvent } from "../../analytics/trackEvent";
import {
  buildConfirmState,
  buildFriendUsers,
  buildPeopleListState,
  getEligibleFriendUsers as selectEligibleFriendUsers,
  getPendingFriendUsers as selectPendingFriendUsers,
} from "./groupsPageSelectors";
import { readErrorDetail } from "./GroupsSharedUi";

function getCountBucket(count) {
  if (count <= 0) return "0";
  if (count === 1) return "1";
  if (count <= 3) return "2-3";
  if (count <= 10) return "4-10";
  return "11+";
}

export default function useGroupMembershipAdminState({
  groups,
  allUsers,
  user,
  token,
  t,
  selectedGroupId,
  activeGroupHomeId,
  setGroups,
  setSelectedGroupId,
  setActiveGroupHomeId,
  resetGroupHomeState,
  upsertGroup,
}) {
  const [actionLoadingById, setActionLoadingById] = useState({});
  const [actionErrorById, setActionErrorById] = useState({});
  const [inviteGroupId, setInviteGroupId] = useState(null);
  const [selectedInviteIds, setSelectedInviteIds] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [peopleListGroupId, setPeopleListGroupId] = useState(null);
  const [peopleListType, setPeopleListType] = useState("members");
  const [groupMemberActionLoadingKey, setGroupMemberActionLoadingKey] =
    useState("");
  const [groupMemberActionError, setGroupMemberActionError] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const groupInvitePanelRef = useRef(null);

  const friendUsers = buildFriendUsers(allUsers, user);
  const {
    peopleListGroup,
    peopleListIds,
    peopleListTitle,
    peopleListCountLabel,
  } = buildPeopleListState({
    groups,
    peopleListGroupId,
    peopleListType,
    t,
  });
  const { confirmGroup, confirmTitle, confirmMessage } = buildConfirmState({
    confirmAction,
    groups,
    allUsers,
    t,
  });

  const resetInviteState = useCallback((nextInviteGroupId = null) => {
    setInviteGroupId(nextInviteGroupId);
    setSelectedInviteIds([]);
    setInviteError("");
  }, []);

  const resetPeopleListState = useCallback(() => {
    setPeopleListGroupId(null);
    setPeopleListType("members");
    setGroupMemberActionError("");
    setGroupMemberActionLoadingKey("");
  }, []);

  const openPeopleList = useCallback((groupId, type) => {
    setPeopleListGroupId(groupId);
    setPeopleListType(type);
    setGroupMemberActionError("");
  }, []);

  const closePeopleList = useCallback(() => {
    resetPeopleListState();
  }, [resetPeopleListState]);

  const openConfirmModal = useCallback((type, group, targetUserId = null) => {
    setConfirmAction({ type, groupId: group.id, targetUserId });
    setConfirmError("");
  }, []);

  const closeConfirmModal = useCallback(
    (force = false) => {
      if (confirmLoading && !force) return;
      setConfirmAction(null);
      setConfirmError("");
    },
    [confirmLoading]
  );

  const isAdmin = useCallback(
    (group) => Boolean(user && group.admins.includes(user.id)),
    [user]
  );

  const getEligibleFriendUsers = useCallback(
    (group) => selectEligibleFriendUsers(friendUsers, group),
    [friendUsers]
  );

  const getPendingFriendUsers = useCallback(
    (group) => selectPendingFriendUsers(friendUsers, group),
    [friendUsers]
  );

  const isMember = useCallback(
    (group) => Boolean(user && group.members.includes(user.id)),
    [user]
  );

  const hasPendingJoinRequest = useCallback(
    (group) => Boolean(user && group.pendingJoinRequests?.includes(user.id)),
    [user]
  );

  const hasGroupInvite = useCallback(
    (group) => Boolean(user && group.invitedUsers?.includes(user.id)),
    [user]
  );

  async function handleGroupMemberManagementAction(group, memberId, action) {
    if (!user || !group?.id || !memberId) return false;

    const actionKey = `${action}:${group.id}:${memberId}`;
    setGroupMemberActionLoadingKey(actionKey);
    setGroupMemberActionError("");

    let endpoint = "";
    let method = "POST";
    let fallbackError = t("groups_member_action_failed", {
      defaultValue: "Could not update group membership.",
    });

    if (action === "promote") {
      endpoint = `${API_BASE}/groups/${group.id}/admins/${memberId}`;
      fallbackError = t("groups_make_admin_failed", {
        defaultValue: "Could not make that member an admin.",
      });
    } else if (action === "demote") {
      endpoint = `${API_BASE}/groups/${group.id}/admins/${memberId}`;
      method = "DELETE";
      fallbackError = t("groups_remove_admin_failed", {
        defaultValue: "Could not remove admin access.",
      });
    } else if (action === "remove") {
      endpoint = `${API_BASE}/groups/${group.id}/members/${memberId}`;
      method = "DELETE";
      fallbackError = t("groups_remove_member_failed", {
        defaultValue: "Could not remove that member from the group.",
      });
    } else {
      setGroupMemberActionLoadingKey("");
      return false;
    }

    try {
      const response = await fetch(
        endpoint,
        withApiCredentials({
          method,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, fallbackError));
      }

      upsertGroup(data, user.id);
      setGroupMemberActionError("");
      window.dispatchEvent(new Event("groupsUpdated"));
      return true;
    } catch (err) {
      setGroupMemberActionError(err.message || fallbackError);
      return false;
    } finally {
      setGroupMemberActionLoadingKey("");
    }
  }

  async function handleMembershipAction(group, action) {
    if (!user) return false;

    setActionLoadingById((prev) => ({ ...prev, [group.id]: true }));
    setActionErrorById((prev) => ({ ...prev, [group.id]: "" }));

    try {
      const response = await fetch(
        `${API_BASE}/groups/${group.id}/${action}`,
        withApiCredentials({
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          readErrorDetail(
            data,
            action === "join" ? t("groups_join_failed") : t("groups_leave_failed")
          )
        );
      }

      upsertGroup(data, user.id);
      if (action === "join") {
        trackProductEvent("group_join_requested", {
          featureArea: "groups",
          metadata: {
            source: "group_membership_action",
            became_member: Array.isArray(data?.members)
              ? data.members.includes(user.id)
              : false,
            pending_request: Array.isArray(data?.pendingJoinRequests)
              ? data.pendingJoinRequests.includes(user.id)
              : false,
          },
        });
      }

      if (activeGroupHomeId === group.id && !data.members.includes(user.id)) {
        setActiveGroupHomeId(null);
        resetPeopleListState();
        resetGroupHomeState();
      }

      window.dispatchEvent(new Event("groupsUpdated"));
      return true;
    } catch (err) {
      setActionErrorById((prev) => ({
        ...prev,
        [group.id]:
          err.message ||
          (action === "join" ? t("groups_join_failed") : t("groups_leave_failed")),
      }));
      return false;
    } finally {
      setActionLoadingById((prev) => ({ ...prev, [group.id]: false }));
    }
  }

  async function handleDeleteGroup(group) {
    if (!user) return false;

    setActionLoadingById((prev) => ({ ...prev, [group.id]: true }));
    setActionErrorById((prev) => ({ ...prev, [group.id]: "" }));

    try {
      const response = await fetch(
        `${API_BASE}/groups/${group.id}`,
        withApiCredentials({
          method: "DELETE",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, t("groups_delete_failed")));
      }

      setGroups((prevGroups) =>
        prevGroups.filter((existingGroup) => existingGroup.id !== group.id)
      );

      if (selectedGroupId === group.id) {
        setSelectedGroupId(null);
        resetPeopleListState();
        resetInviteState(null);
      } else if (inviteGroupId === group.id) {
        resetInviteState(null);
      }

      if (activeGroupHomeId === group.id) {
        setActiveGroupHomeId(null);
        resetPeopleListState();
        resetGroupHomeState();
      }

      window.dispatchEvent(new Event("groupsUpdated"));
      return true;
    } catch (err) {
      setActionErrorById((prev) => ({
        ...prev,
        [group.id]: err.message || t("groups_delete_failed"),
      }));
      return false;
    } finally {
      setActionLoadingById((prev) => ({ ...prev, [group.id]: false }));
    }
  }

  async function handleConfirmAction() {
    if (!confirmAction || !confirmGroup) return;

    setConfirmLoading(true);
    setConfirmError("");

    let success = false;

    if (confirmAction.type === "leave") {
      success = await handleMembershipAction(confirmGroup, "leave");
    } else if (confirmAction.type === "delete") {
      success = await handleDeleteGroup(confirmGroup);
    } else if (
      confirmAction.type === "demoteAdmin" &&
      confirmAction.targetUserId
    ) {
      success = await handleGroupMemberManagementAction(
        confirmGroup,
        confirmAction.targetUserId,
        "demote"
      );
    } else if (
      confirmAction.type === "removeMember" &&
      confirmAction.targetUserId
    ) {
      success = await handleGroupMemberManagementAction(
        confirmGroup,
        confirmAction.targetUserId,
        "remove"
      );
    }

    if (success) {
      closeConfirmModal(true);
    } else {
      setConfirmError(
        confirmAction.type === "delete"
          ? t("groups_delete_failed")
          : confirmAction.type === "leave"
            ? t("groups_leave_failed")
            : confirmAction.type === "demoteAdmin"
              ? t("groups_remove_admin_failed", {
                  defaultValue: "Could not remove admin access.",
                })
              : t("groups_remove_member_failed", {
                  defaultValue: "Could not remove that member from the group.",
                })
      );
    }

    setConfirmLoading(false);
  }

  async function handleInviteFriends(group) {
    if (!user || !selectedInviteIds.length) {
      setInviteError(t("groups_invite_pick_friend"));
      return;
    }

    setInviteLoading(true);
    setInviteError("");

    try {
      const response = await fetch(
        `${API_BASE}/groups/${group.id}/invite`,
        withApiCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ userIds: selectedInviteIds }),
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, t("groups_invite_failed")));
      }

      upsertGroup(data, user.id);
      trackProductEvent("group_invite_sent", {
        featureArea: "groups",
        metadata: {
          source: "group_invite_panel",
          invite_count_bucket: getCountBucket(selectedInviteIds.length),
        },
      });
      setSelectedInviteIds([]);
      setInviteError("");
      window.dispatchEvent(new Event("groupsUpdated"));
    } catch (err) {
      setInviteError(err.message || t("groups_invite_failed"));
    } finally {
      setInviteLoading(false);
    }
  }

  function toggleInviteSelection(friendId) {
    setSelectedInviteIds((prevIds) =>
      prevIds.includes(friendId)
        ? prevIds.filter((id) => id !== friendId)
        : [...prevIds, friendId]
    );
  }

  return {
    actionLoadingById,
    actionErrorById,
    inviteGroupId,
    selectedInviteIds,
    inviteLoading,
    inviteError,
    peopleListGroupId,
    peopleListType,
    groupMemberActionLoadingKey,
    groupMemberActionError,
    confirmAction,
    confirmLoading,
    confirmError,
    groupInvitePanelRef,
    friendUsers,
    peopleListGroup,
    peopleListIds,
    peopleListTitle,
    peopleListCountLabel,
    confirmGroup,
    confirmTitle,
    confirmMessage,
    resetInviteState,
    resetPeopleListState,
    openPeopleList,
    closePeopleList,
    openConfirmModal,
    closeConfirmModal,
    getEligibleFriendUsers,
    getPendingFriendUsers,
    isMember,
    isAdmin,
    hasPendingJoinRequest,
    hasGroupInvite,
    handleGroupMemberManagementAction,
    handleMembershipAction,
    handleDeleteGroup,
    handleConfirmAction,
    handleInviteFriends,
    toggleInviteSelection,
  };
}
